// src/layouts/WorkspaceLayout.tsx
// Unified workspace layout: view switching, filtering, density, search,
// grouping and sorting, with the state held in the URL search params.
//
// The layout draws nothing of its own any more. The tab of the pane showing the
// page already carries its name, so the page has no heading row of its own
// (only an sr-only h1 — a page still needs a title, a screen reader still has
// to hear it), and the controls render into the navbar through a portal. What
// is left on the page is the list.
//
// The same settings go two ways from here: portalled into the navbar, folded
// behind one button, and handed to the right rail, where they open expanded.
// One description, two surfaces — see components/workspace/SettingsSurface.tsx.

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';

import { ScopeCtx } from '@/components/ScopeProvider';
import { SettingsPanel } from '@/components/workspace/SettingsSurface';
import { ViewOptions } from '@/components/workspace/ViewOptions';
import { WorkspaceControls } from '@/components/workspace/WorkspaceControls';
import { PaneControls } from '@/features/tabs/PaneChrome';
import { usePageSettings } from '@/hooks/usePageSettings';
import { WorkspaceCtx, type WorkspaceAction, type WorkspaceContextValue } from '@/features/workspace/context';

import type { WorkspaceConfig, WorkspaceState } from '@/features/workspace/types';

import {
  buildDefaults,
  parseWorkspaceStateFromUrl,
  serializeWorkspaceStateToUrl,
  setView,
  setGroupBy,
  setSortBy,
  setDensity,
  setSearch,
  addFilter,
  removeFilter,
  clearFilters,
} from '@/features/workspace/logic';

export { useWorkspace } from '@/features/workspace/context';
export type { WorkspaceAction } from '@/features/workspace/context';

/** Delay before a keystroke in the search box turns into a request. */
export const SEARCH_DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// URL subscription
// ---------------------------------------------------------------------------

function subscribeToSearchParams(callback: () => void): () => void {
  window.addEventListener('popstate', callback);
  return () => window.removeEventListener('popstate', callback);
}

let _cachedSearch = '';
let _cachedParams = new URLSearchParams('');

function getSearchParamsSnapshot(): URLSearchParams {
  const current = window.location.search;
  if (current !== _cachedSearch) {
    _cachedSearch = current;
    _cachedParams = new URLSearchParams(current);
  }
  return _cachedParams;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function applyStateToUrl(state: WorkspaceState, defaults: WorkspaceState) {
  const params = serializeWorkspaceStateToUrl(state, defaults);

  // Preserve non-workspace params (scope, etc.)
  const current = new URLSearchParams(window.location.search);
  const workspaceKeys = new Set(['view', 'filter', 'group', 'sort', 'dir', 'density', 'q', 'saved']);
  const preserved = new URLSearchParams();
  for (const [key, value] of current.entries()) {
    if (!workspaceKeys.has(key)) {
      preserved.append(key, value);
    }
  }
  for (const [key, value] of params.entries()) {
    preserved.append(key, value);
  }

  const qs = preserved.toString();
  const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
  window.history.replaceState(window.history.state, '', newUrl);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// ---------------------------------------------------------------------------
// Layout component
// ---------------------------------------------------------------------------

interface WorkspaceLayoutProps {
  config: WorkspaceConfig;
  /** i18n key. Kept for the document outline and screen readers only. */
  title: string;
  createLabel?: string;
  onCreateClick?: () => void;
  /**
   * The page's own sections ("Teams | Invitations"), as a segmented control in
   * the settings menu. A second tab row inside the content is a row the pane
   * pays for on every page, for a choice most people make once.
   */
  sections?: ReactNode;
  /**
   * What the list is drawn from, as a <SettingsSection> — e.g. one wrapping a
   * <ScopeOptions />. A section and not a dropdown: a popover opened from
   * inside the settings menu portals to the body, and the menu reads a click in
   * it as a click outside itself.
   */
  scope?: ReactNode;
  /**
   * The saved views, as a <SettingsSection>. A slot rather than a built-in
   * because saved views are a per-product resource (task views, document
   * views); the layout only owns the place they sit in.
   */
  savedViews?: ReactNode;
  /** Compact navbar dropdown of the same saved views, for one-click apply. */
  savedViewsMenu?: ReactNode;
  children: ReactNode;
}

export const WorkspaceLayout = observer(({
  config,
  title,
  createLabel,
  onCreateClick,
  sections,
  scope,
  savedViews,
  savedViewsMenu,
  children,
}: WorkspaceLayoutProps) => {
  const { t } = useI18n();

  // Read state from URL
  const searchParams = useSyncExternalStore(subscribeToSearchParams, getSearchParamsSnapshot);

  const defaults = useMemo<WorkspaceState>(() => buildDefaults(config), [config]);

  const state = useMemo(
    () => parseWorkspaceStateFromUrl(searchParams, config),
    [searchParams, config],
  );

  const dispatch = useCallback(
    (action: WorkspaceAction) => {
      let next: WorkspaceState;
      switch (action.type) {
        case 'SET_VIEW':
          next = setView(state, action.view);
          break;
        case 'SET_GROUP_BY':
          next = setGroupBy(state, action.groupBy);
          break;
        case 'SET_SORT_BY':
          next = setSortBy(state, action.field, action.direction);
          break;
        case 'SET_DENSITY':
          next = setDensity(state, action.density);
          break;
        case 'SET_SEARCH':
          next = setSearch(state, action.search);
          break;
        case 'ADD_FILTER':
          next = addFilter(state, action.filter);
          break;
        case 'REMOVE_FILTER':
          next = removeFilter(state, action.field);
          break;
        case 'CLEAR_FILTERS':
          next = clearFilters(state);
          break;
        case 'SET_STATE':
          next = action.state;
          break;
        default:
          return;
      }
      applyStateToUrl(next, defaults);
    },
    [state, defaults],
  );

  // ── Debounced search ──────────────────────────────────────────────────
  // The input stays fully responsive while only one request is issued per
  // pause in typing, instead of one per keystroke.
  const [searchDraft, setSearchDraft] = useState(state.search);
  const committedSearch = useRef(state.search);

  // Adopt search changes that came from outside the input (back/forward,
  // a shared URL, a programmatic clear-filters).
  useEffect(() => {
    if (state.search !== committedSearch.current) {
      committedSearch.current = state.search;
      setSearchDraft(state.search);
    }
  }, [state.search]);

  useEffect(() => {
    if (searchDraft === committedSearch.current) return;
    const timer = window.setTimeout(() => {
      committedSearch.current = searchDraft;
      dispatch({ type: 'SET_SEARCH', search: searchDraft });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchDraft, dispatch]);

  const contextValue = useMemo<WorkspaceContextValue>(
    () => ({
      state,
      defaults,
      config,
      dispatch,
      searchDraft,
      onSearchDraftChange: setSearchDraft,
    }),
    [state, defaults, config, dispatch, searchDraft],
  );

  // The rail renders this where none of the page's providers reach, so the
  // contexts its sections read travel with it. Memoised because it is stored,
  // and a new element on every render would re-render the panel for nothing.
  const scopeCtx = useContext(ScopeCtx);
  const railSettings = useMemo(
    () => (
      <ScopeCtx.Provider value={scopeCtx}>
        <WorkspaceCtx.Provider value={contextValue}>
          <SettingsPanel>
            {sections && <div className="pb-1">{sections}</div>}
            {scope}
            {savedViews}
            <ViewOptions />
          </SettingsPanel>
        </WorkspaceCtx.Provider>
      </ScopeCtx.Provider>
    ),
    [scopeCtx, contextValue, sections, scope, savedViews],
  );
  usePageSettings(railSettings);

  return (
    <WorkspaceCtx.Provider value={contextValue}>
      {/* The page still has a heading; the tab is what shows it. */}
      <h1 className="sr-only">{t(title)}</h1>

      <PaneControls>
        <WorkspaceControls
          sections={sections}
          scope={scope}
          savedViews={savedViews}
          savedViewsMenu={savedViewsMenu}
          createLabel={createLabel}
          onCreateClick={onCreateClick}
        />
      </PaneControls>

      {/* h-full only binds when a page above claimed the pane's full surface
          (useFullPaneSurface); on ordinary document pages the ancestors are
          auto-height and this resolves to auto, i.e. a no-op there. */}
      <div role="tabpanel" className="h-full min-h-0">{children}</div>
    </WorkspaceCtx.Provider>
  );
});
